import React, { useState, useEffect } from 'react'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { fetchWorkspaces } from '@/store/workspaceSlice'
import Header, { HeaderProps } from '../../ui/Header'
import Sidebar, { SidebarProps } from '../../ui/Sidebar'
import NoWorkspaceScreen from '../NoWorkspaceScreen'

export interface LayoutProps {
  children: React.ReactNode
  sidebarProps?: Omit<SidebarProps, 'className' | 'isExpanded' | 'onToggle'>
  headerProps?: Omit<HeaderProps, 'className'>
  className?: string
}

const Layout: React.FC<LayoutProps> = ({
  children,
  sidebarProps = {},
  headerProps = {},
  className = ''
}) => {
  const dispatch = useAppDispatch()
  const [isExpanded, setIsExpanded] = useState(true)
  const { workspaces, isLoading, hasInitialized } = useAppSelector(state => state.workspace)

  const handleToggle = () => {
    setIsExpanded(!isExpanded)
  }

  // Fallback: Ensure workspaces are loaded if not already initialized
  // (This handles edge cases where auth initialization might not have triggered workspace fetching)
  useEffect(() => {
    if (!hasInitialized && !isLoading) {
      dispatch(fetchWorkspaces({}))
    }
  }, [dispatch, hasInitialized, isLoading])


  // Show loading state while fetching workspaces for the first time
  if (!hasInitialized) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div>Loading...</div>
      </div>
    )
  }

  // Show no workspace screen if no workspaces available after initialization
  if (hasInitialized && workspaces.length === 0) {
    return <NoWorkspaceScreen />
  }

  return (
    <div className={`min-h-screen bg-white ${className}`}>
      {/* Fixed Sidebar */}
      <Sidebar 
        {...sidebarProps}
        isExpanded={isExpanded}
        onToggle={handleToggle}
        className="fixed top-0 left-0 z-40 h-screen"
      />

      {/* Fixed Header */}
      <Header 
        {...headerProps}
        className={`fixed top-0 z-30 transition-all duration-300 ease-in-out ${
          isExpanded ? 'left-[270px] w-[calc(100%-270px)]' : 'left-[70px] w-[calc(100%-70px)]'
        }`}
      />

      {/* Main Content Area */}
      <main 
        className={`transition-all duration-300 ease-in-out pt-[70px] ${
          isExpanded ? 'ml-[270px]' : 'ml-[70px]'
        }`}
      >
        {/* overflow-auto 금지: min-h 특성상 이 div는 콘텐츠만큼 늘어나기만 하고
            실제 스크롤은 문서(body)에서 일어난다. 여기에 overflow를 주면
            sticky의 기준 조상이 이 div가 되어 페이지 내 sticky 헤더가 동작하지 않는다. */}
        <div className="min-h-[calc(100vh-70px)]">
          {children}
        </div>
      </main>
    </div>
  )
}

export default Layout